import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listCashEquivalentLedgers, getIncomeSummary } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `general-accounts-report-income-summary` —
 * for one chosen cash/bank ledger, which Income-category ledgers fed money
 * into it (a correlated-subquery join: Income ledgers credited in the same
 * voucher where the chosen ledger was debited). The old app's Java mapping
 * had a real bug here (both debit and credit set from the same total_credit
 * column, and the response-level grand totals never populated) — this port
 * fixes both (single `amount` per row, grand total summed client-side)
 * rather than reproducing them, per this migration's "documented
 * simplification, not bug-for-bug" convention.
 */
export default function IncomeSummaryPage() {
  const [ledgers, setLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState("");
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listCashEquivalentLedgers().then((list) => {
      setLedgers(list);
      if (list.length > 0) setLedgerId(String(list[0].ledgerId));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    if (!ledgerId) {
      setError("Please select a ledger.");
      return;
    }
    setLoading(true);
    try {
      setRows(await getIncomeSummary(fromDate, toDate, ledgerId));
    } catch (err) {
      setError("Failed to load income summary.");
    } finally {
      setLoading(false);
    }
  };

  const total = (rows || []).reduce((s, r) => s + r.amount, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Income Summary</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Ledger
          <select value={ledgerId} onChange={(e) => setLedgerId(e.target.value)}>
            {ledgers.map((l) => (
              <option key={l.ledgerId} value={l.ledgerId}>
                {l.ledgerName}
              </option>
            ))}
          </select>
        </label>
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Income Ledger</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.ledgerId}>
                    <td>{r.ledgerName}</td>
                    <td>{r.amount.toFixed(2)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={2}>No income found funding this ledger in this period.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 800 }}>Grand Total</td>
                  <td>
                    <strong>{total.toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
